// FILE: src/shared/hooks/use-dadata.ts
// VERSION: 1.0.0
// PURPOSE: React-хук для автозаполнения данных компании по ИНН и банка по БИК через DaData.

import { useState, useCallback, useRef } from 'react';
import { DaDataService, type DaDataCompany, type DaDataBank } from '@oxpa/shared/services/dadata';

// TYPES: Результат поиска компании
export interface CompanyResult {
  name: string;
  fullName: string;
  inn: string;
  kpp: string;
  ogrn: string;
  address: string;
  director: string;
  directorPost: string;
  type: 'LEGAL' | 'INDIVIDUAL';
  status: string;
}

// TYPES: Результат поиска банка
export interface BankResult {
  name: string;
  fullName: string;
  bic: string;
  correspondentAccount: string;
  inn: string;
  address: string;
  status: string;
}

// FUNC: Преобразование ответа DaData в удобный формат
function mapCompany(item: DaDataCompany): CompanyResult {
  return {
    name: item.data.name?.short_with_opf || item.value || '',
    fullName: item.data.name?.full_with_opf || '',
    inn: item.data.inn || '',
    kpp: item.data.kpp || '',
    ogrn: item.data.ogrn || '',
    address: item.data.address?.unrestricted_value || item.data.address?.value || '',
    director: item.data.management?.name || '',
    directorPost: item.data.management?.post || '',
    type: item.data.type || 'LEGAL',
    status: item.data.state?.status || '',
  };
}

function mapBank(item: DaDataBank): BankResult {
  return {
    name: item.data.name?.short || item.value || '',
    fullName: item.data.name?.full || '',
    bic: item.data.bic || '',
    correspondentAccount: item.data.correspondent_account || '',
    inn: item.data.inn || '',
    address: item.data.address?.value || '',
    status: item.data.state?.status || '',
  };
}

// FUNC: Хук для поиска компании по ИНН
export function useDaDataCompany() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CompanyResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const searchByInn = useCallback(async (inn: string) => {
    const cleanInn = inn.replace(/\D/g, '');
    
    // ИНН юр. лица = 10 цифр, ИП = 12 цифр
    if (cleanInn.length < 10) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await DaDataService.findCompanyByInn(cleanInn);
      setResults(data.map(mapCompany));
    } catch {
      setError('Ошибка поиска по ИНН');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // С debounce (для автозаполнения при наборе)
  const searchByInnDebounced = useCallback((inn: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchByInn(inn), 400);
  }, [searchByInn]);

  const reset = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return { loading, results, error, searchByInn, searchByInnDebounced, reset };
}

// FUNC: Хук для подсказок компаний по произвольному запросу (название, ИНН)
export function useDaDataCompanySuggest() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CompanyResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const suggest = useCallback(async (query: string) => {
    if (!query || query.length < 3) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await DaDataService.suggestCompany(query);
      setResults(data.map(mapCompany));
    } catch {
      setError('Ошибка поиска подсказок');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // С debounce
  const suggestDebounced = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => suggest(query), 400);
  }, [suggest]);

  const reset = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return { loading, results, error, suggest, suggestDebounced, reset };
}

// FUNC: Хук для поиска банка по БИК
export function useDaDataBank() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<BankResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const searchByBik = useCallback(async (bik: string) => {
    const cleanBik = bik.replace(/\D/g, '');
    
    // БИК = 9 цифр
    if (cleanBik.length < 9) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await DaDataService.findBankByBik(cleanBik);
      setResults(data.map(mapBank));
    } catch {
      setError('Ошибка поиска по БИК');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // С debounce
  const searchByBikDebounced = useCallback((bik: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchByBik(bik), 400);
  }, [searchByBik]);

  const reset = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return { loading, results, error, searchByBik, searchByBikDebounced, reset };
}
